import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { setMessage } from '../message';
import FileServices from '../../services/fileServices';
import { clearAuth } from 'store/auth';

const onProgress = (thunkAPI) => (value) => {
  thunkAPI.dispatch(setProgres(value));
};

export const getList = createAsyncThunk(
  'file/list',
  async ({ type, page, perPage, sort, orderBy, search, jenjang_id, mapel_id, bab_id, created_by, start_date, end_date }, thunkAPI) => {
    try {
      const response = await FileServices.getList(
        type,
        page,
        perPage,
        sort,
        orderBy,
        search,
        jenjang_id,
        mapel_id,
        bab_id,
        created_by,
        start_date,
        end_date
      );
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        thunkAPI.dispatch(clearAuth());
      }
      const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
      thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
      return thunkAPI.rejectWithValue();
    }
  }
);

export const getListArchive = createAsyncThunk(
  'file/archive',
  async ({ type, page, perPage, sort, orderBy, search, jenjang_id, mapel_id, bab_id, created_by, start_date, end_date }, thunkAPI) => {
    try {
      const response = await FileServices.getListArchive(
        type,
        page,
        perPage,
        sort,
        orderBy,
        search,
        jenjang_id,
        mapel_id,
        bab_id,
        created_by,
        start_date,
        end_date
      );
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        thunkAPI.dispatch(clearAuth());
      }
      const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
      thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
      return thunkAPI.rejectWithValue();
    }
  }
);

export const createData = createAsyncThunk('file/create', async (data, thunkAPI) => {
  try {
    const response = await FileServices.create(data, onProgress(thunkAPI));
    return response.data;
  } catch (error) {
    console.log(error);
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    if (error?.response?.status === 401) {
      thunkAPI.dispatch(clearAuth());
    }
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const readData = createAsyncThunk('file/read', async (id, thunkAPI) => {
  try {
    const response = await FileServices.read(id);
    return response.data;
  } catch (error) {
    if (error.response.status === 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const updateData = createAsyncThunk('file/update', async ({ id, data }, thunkAPI) => {
  try {
    const response = await FileServices.update(id, data, onProgress(thunkAPI));

    return response.data;
  } catch (error) {
    if (error.response.status === 401) {
      thunkAPI.dispatch(clearAuth());
    }

    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const deleteData = createAsyncThunk('file/delete', async (id, thunkAPI) => {
  try {
    const response = await FileServices.del(id);

    return response.data;
  } catch (error) {
    if (error.response.status === 401) {
      thunkAPI.dispatch(clearAuth());
    }

    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const changeStatus = createAsyncThunk('file/changeStatus', async (data, thunkAPI) => {
  try {
    const response = await FileServices.changeStatus(data.id, data);
    return response.data;
  } catch (error) {
    if (error.response.status === 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue();
  }
});

const initialState = { isLoading: false, list: [], data: [], group: [], error: [], progress: 0 };

const fileSlice = createSlice({
  name: 'file',
  initialState,
  reducers: {
    setProgres: (state, action) => {
      return {
        ...state,
        progress: action.payload
      };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getList.pending, (state) => {
      state.isLoading = true;
    }),
      builder.addCase(getList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = action.payload;
      }),
      builder.addCase(getList.rejected, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(getListArchive.pending, (state) => {
        state.isLoading = true;
      }),
      builder.addCase(getListArchive.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = action.payload;
      }),
      builder.addCase(getListArchive.rejected, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(createData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      }),
      builder.addCase(createData.fulfilled, (state) => {
        state.isLoading = false;
        state.progress = 0;
      }),
      builder.addCase(createData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action?.payload?.errors ? action.payload.errors : action.payload;
      }),
      builder.addCase(readData.pending, (state) => {
        state.isLoading = true;
      }),
      builder.addCase(readData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
        state.error = [];
      }),
      builder.addCase(readData.rejected, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(updateData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      }),
      builder.addCase(updateData.fulfilled, (state) => {
        state.isLoading = false;
        state.progress = 0;
      }),
      builder.addCase(updateData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.errors ? action.payload.errors : action.payload;
      }),
      builder.addCase(deleteData.pending, (state) => {
        state.isLoading = true;
      }),
      builder.addCase(deleteData.fulfilled, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(deleteData.rejected, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(changeStatus.pending, (state) => {
        state.isLoading = true;
      }),
      builder.addCase(changeStatus.fulfilled, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(changeStatus.rejected, (state) => {
        state.isLoading = false;
      });
  }
});

const { reducer, actions } = fileSlice;
export const { setProgres } = actions;

export default reducer;
